import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, Calendar, ArrowLeft, ArrowRight, AlertCircle, FileText, FileSpreadsheet, File, Lock, Unlock, Plus, Trash2, AlertTriangle, Send, Search, Edit, Copy, QrCode, Clock, CheckCircle2, ArrowRightLeft, ExternalLink, Wallet, FileDown, CalendarClock } from "lucide-react";
import api, { handleApiError, flattenDetail, formatCurrency, formatDate, formatDateTime, brMonthStr } from "@/lib/api";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/usePermission";
import Pagination from "@/components/Pagination";
import CurrencyInput from "@/components/CurrencyInput";
import { useNavigate } from "react-router-dom";

const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];


function DiscountCategorySelect({ value, onChange, options, placeholder, customPlaceholder }) {
  const [editing, setEditing] = useState(false);
  const isCustom = value === "__custom__" || (value && !options.includes(value));

  if (editing || isCustom) {
    return (
      <div className="flex gap-2">
        <Input data-testid="discount-description-input" value={value === "__custom__" ? "" : value}
          onChange={(e) => onChange(e.target.value)} placeholder={customPlaceholder}
          className="bg-black/20 border-white/10 flex-1" autoFocus />
        <Button type="button" variant="outline" size="sm" className="border-white/10 text-xs shrink-0"
          onClick={() => { setEditing(false); onChange(""); }}>Voltar</Button>
      </div>
    );
  }

  return (
    <Select value={options.includes(value) ? value : ""} onValueChange={(v) => {
      if (v === "__outro__") { setEditing(true); onChange(""); }
      else onChange(v);
    }}>
      <SelectTrigger data-testid="discount-category-select" className="bg-black/20 border-white/10">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        <SelectItem value="__outro__">Outro (digitar)</SelectItem>
      </SelectContent>
    </Select>
  );
}


export default function CompanyFechamento() {
  const brMonth = brMonthStr(); // "YYYY-MM" in BRT
  // Default to PREVIOUS month (the competência being paid in the current month).
  // E.g. in May we show April's closing (which is paid in May).
  const currentYear = Number(brMonth.split("-")[0]);
  const currentMonth = Number(brMonth.split("-")[1]);
  const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discountDialog, setDiscountDialog] = useState(null);
  const [discountForm, setDiscountForm] = useState({ description: "", amount: "", entry_type: "desconto", value_type: "fixed", recurrence: "unico", recurrence_months: "" });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenDialog, setReopenDialog] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [sendingPayment, setSendingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [justGeneratedPix, setJustGeneratedPix] = useState(false);
  const navigate = useNavigate();
  const [releasingCustody, setReleasingCustody] = useState(false);
  const [editDiscountDialog, setEditDiscountDialog] = useState(null);
  const { canEdit } = usePermission("fechamento");
  const [paymentDatePf, setPaymentDatePf] = useState("");
  const [paymentDatePj, setPaymentDatePj] = useState("");
  const [fechPage, setFechPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const monthStr = `${year}-${String(month).padStart(2, "0")}`;
  // Payment/liquidation month = competência + 1
  const liquidationMonth = month === 12 ? 1 : month + 1;
  const liquidationYear = month === 12 ? year + 1 : year;
  // Detect if the user is about to close a month that's ahead of what's expected
  const expectedCompetencia = currentMonth === 1 ? 12 : currentMonth - 1;
  const expectedCompetenciaYear = currentMonth === 1 ? currentYear - 1 : currentYear;
  const isAheadOfExpected = year > expectedCompetenciaYear || (year === expectedCompetenciaYear && month > expectedCompetencia);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ month: monthStr, page: fechPage, page_size: 20 });
    if (searchFilter) params.set("search", searchFilter);
    if (deptFilter !== "all") params.set("department", deptFilter);
    if (typeFilter !== "all") params.set("emp_type", typeFilter);
    if (sortField) { params.set("sort_by", sortField); params.set("sort_dir", sortDir); }
    try {
      const res = await api.get(`/fechamento?${params}`);
      setData(res.data);
    } catch (err) { toast.error(handleApiError(err, "Erro ao carregar fechamento")); }
    setLoading(false);
  }, [monthStr, fechPage, searchFilter, deptFilter, typeFilter, sortField, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMonth = () => { setFechPage(1); if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { setFechPage(1); if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const isCurrent = `${year}-${String(month).padStart(2, "0")}` === brMonthStr();

  const handleExport = async (format) => {
    try {
      const res = await api.get(`/fechamento/export?month=${monthStr}&format=${format}`);
      toast.info(res.data.message);
      if (res.data.job_id) {
        pollJob(res.data.job_id);
      }
    } catch { toast.error("Erro ao exportar relatório"); }
  };

  const handleAddDiscount = async () => {
    const desc = discountForm.description === "__custom__" ? "" : discountForm.description;
    if (!desc || !discountForm.amount) { toast.error("Preencha todos os campos"); return; }
    if (discountForm.recurrence === "recorrente_prazo" && (!discountForm.recurrence_months || parseInt(discountForm.recurrence_months) < 1)) { toast.error("Informe a quantidade de meses"); return; }
    setSavingDiscount(true);
    try {
      await api.post("/fechamento/discounts", {
        employee_id: discountDialog,
        competency_month: monthStr,
        description: desc,
        amount: parseFloat(discountForm.amount) || 0,
        entry_type: discountForm.entry_type,
        value_type: discountForm.value_type,
        recurrence: discountForm.recurrence,
        recurrence_months: discountForm.recurrence === "recorrente_prazo" ? parseInt(discountForm.recurrence_months) : null,
      });
      toast.success(`${discountForm.entry_type === "desconto" ? "Desconto" : discountForm.entry_type === "acrescimo" ? "Acréscimo" : "Despesa acessória"}: ${discountForm.description} adicionado`);
      setDiscountDialog(null);
      setDiscountForm({ description: "", amount: "", entry_type: "desconto", value_type: "fixed", recurrence: "unico", recurrence_months: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar"); }
    setSavingDiscount(false);
  };

  const handleRemoveDiscount = async (discountId) => {
    if (!window.confirm("Tem certeza que deseja remover este lançamento?")) return;
    try {
      await api.delete(`/fechamento/discounts/${discountId}`);
      toast.success("Desconto removido");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const openCloseDialog = async () => {
    try {
      const res = await api.get(`/fechamento/payment-dates?month=${monthStr}`);
      setPaymentDatePf(res.data.payment_date_pf || "");
      setPaymentDatePj(res.data.payment_date_pj || "");
    } catch { /* use empty */ }
    setCloseDialog(true);
  };

  const [jobProgress, setJobProgress] = useState(null);

  const pollJob = async (jobId, onComplete) => {
    const poll = async () => {
      try {
        const res = await api.get(`/fechamento/job/${jobId}`);
        const job = res.data;
        setJobProgress(job);
        if (job.status === "completed") {
          toast.success(job.message);
          setJobProgress(null);
          onComplete?.();
          fetchData();
        } else if (job.status === "failed") {
          toast.error(job.message || "Erro no processamento");
          setJobProgress(null);
          onComplete?.();
          fetchData();
        } else {
          setTimeout(poll, 1500);
        }
      } catch {
        setJobProgress(null);
        onComplete?.();
      }
    };
    poll();
  };

  const handleCloseMonth = async () => {
    setClosing(true);
    try {
      const res = await api.post("/fechamento/close", { month: monthStr, payment_date_pf: paymentDatePf, payment_date_pj: paymentDatePj });
      setCloseDialog(false);
      toast.info("Fechamento iniciado...");
      pollJob(res.data.job_id, () => setClosing(false));
    } catch (err) {
      // C1/C7: com a régua estrita da data, o `close` passou a devolver 422 com
      // `detail` em LISTA (`[{campo, mensagem}]`). Jogar a lista crua no toast é
      // erro de child do React — `flattenDetail` é a régua única do repo, já
      // usada no fluxo do CNAB abaixo.
      toast.error(flattenDetail(err.response?.data?.detail) || "Erro ao fechar mês");
      setClosing(false);
    }
  };

  const handleReopenMonth = async () => {
    setReopening(true);
    try {
      await api.post("/fechamento/reopen", { month: monthStr });
      toast.success("Mês reaberto com sucesso!");
      setReopenDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao reabrir mês"); }
    setReopening(false);
  };

  const [paymentMenuOpen, setPaymentMenuOpen] = useState(false);
  const [generatingCnab, setGeneratingCnab] = useState(null);

  const handleGenerateCnab = async (fmt) => {
    setGeneratingCnab(fmt);
    try {
      const res = await api.post("/fechamento/generate-cnab", { month: monthStr, format: fmt }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kredz-folha-${monthStr}-cnab${fmt}.rem`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Arquivo CNAB ${fmt} baixado com sucesso`);
    } catch (err) {
      let msg = "Erro ao gerar CNAB";
      try {
        if (err.response?.data instanceof Blob) {
          // Único corpo de erro do front que o interceptor de `lib/api.js` NÃO
          // alcança: com `responseType: "blob"` o `data` chega como Blob, então
          // `_normalizeStructuredDetail` não vê `data.detail` e o 422
          // estruturado sobrevive como ARRAY até aqui. Achatar com a MESMA
          // régua (`flattenDetail`) — sem ela o toast mostraria
          // "[object Object]".
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = flattenDetail(parsed?.detail) || msg;
        } else {
          // Já passou pelo interceptor (string); `flattenDetail` é idempotente.
          msg = flattenDetail(err.response?.data?.detail) || msg;
        }
      } catch { /* parse failed, use default */ }
      // Pendência de CADASTRO chega aqui como um diagnóstico multi-linha (uma
      // seção por classe, uma linha por favorecido, "+N outras" quando o teto
      // de 20 cortou). Sem componente novo: o próprio toast passa a respeitar
      // as quebras (`pre-line` — o default do sonner é colapsar) e a durar o
      // suficiente para o operador ler a lista e anotar quem corrigir, com
      // botão de fechar para quem já leu. Erro de uma linha só (formato
      // inválido, mês não fechado) sai exatamente como antes.
      const multilinha = msg.includes("\n");
      toast.error(msg, multilinha
        ? { duration: 30000, closeButton: true, style: { whiteSpace: "pre-line" } }
        : undefined);
    }
    setGeneratingCnab(null);
  };

  const handleSendPayment = async () => {
    setSendingPayment(true);
    try {
      const res = await api.post("/fechamento/send-payment", { month: monthStr });
      toast.info("Gerando PIX para pagamento...");
      setJustGeneratedPix(true);
      pollJob(res.data.job_id, () => setSendingPayment(false));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar para pagamento");
      setSendingPayment(false);
    }
  };

  // Auto-open QR Code dialog when a fresh PIX arrives in fetchData after send-payment
  useEffect(() => {
    if (justGeneratedPix && data?.sent_for_payment && (data?.pix_url || data?.pix_emv)) {
      setShowQrDialog(true);
      setJustGeneratedPix(false);
    }
  }, [justGeneratedPix, data]);

  const copyPixCode = async () => {
    const code = data?.pix_emv || data?.pix_qr_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código PIX copiado");
    } catch {
      toast.error("Não foi possível copiar o código PIX");
    }
  };

  const handleReleaseCustodyAll = async () => {
    if (!window.confirm("Liberar custódia de todos os colaboradores neste mês?")) return;
    setReleasingCustody(true);
    try {
      const res = await api.post("/fechamento/release-custody", { month: monthStr });
      toast.info(res.data.message || "Liberação de custódia iniciada");
      pollJob(res.data.job_id, () => setReleasingCustody(false));
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao liberar custódia"); }
    if (!jobProgress) setReleasingCustody(false);
  };

  const handleReleaseCustodyEmployee = async (employeeId) => {
    try {
      const res = await api.post(`/fechamento/release-custody/${employeeId}`, { month: monthStr });
      toast.info(res.data.message || "Liberação de custódia iniciada");
      pollJob(res.data.job_id, fetchData);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao liberar custódia"); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>;

  const t = data?.totals || {};
  const custodyItems = data?.custody_items || [];
  const hasCustody = custodyItems.some(c => c.blocked);
  const paymentResults = data?.payment_results || {};
  const transferPlan = paymentResults?.transfer_plan || [];
  const custodyReleased = custodyItems.filter(c => !c.blocked && c.release_status === "transfer_created").length;
  const transferCreated = transferPlan.filter(t => ["created", "success", "processing"].includes(t.status)).length + custodyReleased;
  const transferBlocked = transferPlan.filter(t => String(t.status || "").startsWith("blocked") || t.status === "failed").length + custodyItems.filter(c => c.release_status === "failed" || c.release_status === "blocked_missing_bank_data").length;
  const blockedCustody = custodyItems.filter(c => c.blocked).length;
  // C7 — as pernas AGENDADAS: planejadas, ainda NÃO criadas no provedor, saem
  // sozinhas na data do tipo delas. Sem este número o card de status mostrava
  // "3 iniciadas" numa folha de 8 e o resto sumia da tela.
  const transferScheduled = transferPlan.filter(t => String(t.status || "").toLowerCase() === "scheduled");
  const scheduledDates = [...new Set(transferScheduled.map(t => t.due_date).filter(Boolean))].sort();
  const hasAnyCashouts = paymentResults?.transfers_started ||
    transferPlan.some(t => ["created", "processing", "success"].includes(t.status)) ||
    custodyItems.some(c => !c.blocked && ["transfer_created", "success"].includes(c.release_status));
  const isPaidOrLiquidated = data?.boleto_status === "paid_pix_received" || data?.boleto_status === "paid" || data?.boleto_status === "liquidated" || paymentResults?.transfers_started;
  const paymentStatus = (() => {
    if (!data?.sent_for_payment) return { label: "Não enviado", tone: "muted", icon: Clock };
    if (paymentResults?.transfers_started) return { label: "Transfers iniciadas", tone: "blue", icon: ArrowRightLeft };
    if (data?.boleto_status === "paid_pix_received") return { label: "PIX pago", tone: "emerald", icon: CheckCircle2 };
    return { label: "Aguardando pagamento PIX", tone: "amber", icon: Clock };
  })();
  const PaymentStatusIcon = paymentStatus.icon;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="company-fechamento">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight" data-testid="fechamento-title">Fechamento Mensal</h1>
          <p className="text-sm text-muted-foreground mt-1">Controle de folha, descontos e antecipações</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && !data?.is_closed && (
            <Button data-testid="close-month-btn" onClick={openCloseDialog}
              disabled={!data?.is_editable || !data?.items?.length}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold disabled:opacity-40"
              title={!data?.is_editable ? "Não é possível fechar: existe um mês posterior já fechado" : ""}>
              <Lock className="w-4 h-4 mr-2" /> Fechar Mês
            </Button>
          )}
          {canEdit && data?.is_closed && !data?.sent_for_payment && (
            <Button data-testid="reopen-month-btn" onClick={() => setReopenDialog(true)}
              variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 font-semibold">
              <Unlock className="w-4 h-4 mr-2" /> Reabrir Mês
            </Button>
          )}
          {canEdit && data?.is_closed && data?.sent_for_payment && !hasAnyCashouts && data?.boleto_status !== "paid" && data?.boleto_status !== "liquidated" && (
            <Button data-testid="reopen-paid-month-btn" onClick={() => setReopenDialog(true)}
              variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 font-semibold">
              <Unlock className="w-4 h-4 mr-2" /> Reabrir e Cancelar PIX/Boleto
            </Button>
          )}
          {canEdit && data?.is_closed && data?.sent_for_payment && hasAnyCashouts && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button data-testid="reopen-blocked-btn" disabled
                      variant="outline" className="border-white/10 text-muted-foreground opacity-50 cursor-not-allowed font-semibold">
                      <Lock className="w-4 h-4 mr-2" /> Reabrir Mês
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Não é possível reabrir — já existem repasses (transfers) efetivados. Ajustes podem ser feitos na aba Pagamentos.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {!isPaidOrLiquidated && (
          <div className="relative" data-testid="payment-menu-wrapper">
            <Button data-testid="send-payment-btn"
              disabled={!data?.is_closed || sendingPayment || generatingCnab || data?.sent_for_payment}
              onClick={() => data?.sent_for_payment ? null : setPaymentMenuOpen(!paymentMenuOpen)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-40">
              {sendingPayment ? <div className="loading-spinner" /> : <><Send className="w-4 h-4 mr-2" /> Enviar para Pagamento</>}
            </Button>
            {paymentMenuOpen && !data?.sent_for_payment && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setPaymentMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-white/10 bg-zinc-900 shadow-xl z-50 p-1.5 animate-fade-in"
                   data-testid="payment-method-menu">
                <button data-testid="payment-pix-btn" onClick={() => { setPaymentMenuOpen(false); handleSendPayment(); }}
                  className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-blue-600/15 transition-colors text-left">
                  <div className="w-9 h-9 rounded-lg bg-blue-600/20 flex items-center justify-center shrink-0">
                    <QrCode className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Gerar PIX</p>
                    <p className="text-[11px] text-muted-foreground">QR Code para pagamento via StarkBank</p>
                  </div>
                </button>
                <div className="h-px bg-white/5 my-1" />
                <button data-testid="payment-cnab240-btn"
                  disabled={!!generatingCnab}
                  onClick={() => { setPaymentMenuOpen(false); handleGenerateCnab("240"); }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-white/[0.04] transition-colors text-left disabled:opacity-40">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600/15 flex items-center justify-center shrink-0">
                    <FileDown className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium">CNAB 240</p>
                    <p className="text-[10px] text-muted-foreground">Formato Febraban moderno</p>
                  </div>
                </button>
                <button data-testid="payment-cnab400-btn"
                  disabled={!!generatingCnab}
                  onClick={() => { setPaymentMenuOpen(false); handleGenerateCnab("400"); }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-white/[0.04] transition-colors text-left disabled:opacity-40">
                  <div className="w-8 h-8 rounded-lg bg-amber-600/15 flex items-center justify-center shrink-0">
                    <FileDown className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium">CNAB 400</p>
                    <p className="text-[10px] text-muted-foreground">Formato legado</p>
                  </div>
                </button>
              </div>
              </>
            )}
          </div>
          )}
          {canEdit && hasCustody && (
            <Button data-testid="release-custody-all-btn" onClick={handleReleaseCustodyAll} disabled={releasingCustody}
              variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 font-semibold">
              {releasingCustody ? <div className="loading-spinner" /> : <><Unlock className="w-4 h-4 mr-2" /> Liberar Custódia</>}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-fechamento-btn" disabled={!data?.items?.length}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="export-csv-btn" onClick={() => handleExport("csv")}>
                <FileText className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-xlsx-btn" onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-pdf-btn" onClick={() => handleExport("pdf")}>
                <File className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {jobProgress && (
        <Card className="border-blue-500/20 bg-blue-500/5 animate-fade-in" data-testid="job-progress">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">{jobProgress.message}</p>
              <span className="text-xs text-blue-400 font-mono">{jobProgress.progress}%</span>
            </div>
            <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${jobProgress.progress}%` }} />
            </div>
          </CardContent>
        </Card>
      )}

      {data?.sent_for_payment && (data?.pix_emv || data?.pix_url || data?.boleto_pdf) && (() => {
        const isPaid = data.boleto_status === "paid_pix_received" || paymentResults?.transfers_started;
        const cardBorder = isPaid ? "border-blue-500/25 bg-blue-500/5" : "border-emerald-500/20 bg-emerald-500/5";
        return (
        <Card className={cardBorder} data-testid="payment-pix-card">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${transferScheduled.length > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`} data-testid="payment-status-summary">
                <div className="rounded-md border border-white/10 bg-black/20 p-3" data-testid="payment-status-current">
                  <p className="text-[11px] uppercase text-muted-foreground mb-1">Status</p>
                  <p className={`text-sm font-semibold flex items-center gap-2 ${isPaid ? "text-blue-400" : ""}`}><PaymentStatusIcon className="w-4 h-4" /> {paymentStatus.label}</p>
                </div>
                <div className="rounded-md border border-white/10 bg-black/20 p-3" data-testid="payment-status-transfers-created">
                  <p className="text-[11px] uppercase text-muted-foreground mb-1">Transfers</p>
                  <p className="text-sm font-semibold text-blue-300">{transferCreated} iniciada(s)</p>
                </div>
                {transferScheduled.length > 0 && (
                  <div className="rounded-md border border-cyan-500/20 bg-cyan-500/[0.06] p-3" data-testid="payment-status-scheduled">
                    <p className="text-[11px] uppercase text-muted-foreground mb-1">Agendados</p>
                    <p className="text-sm font-semibold text-cyan-300">{transferScheduled.length} aguardando data</p>
                    {scheduledDates.length > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="payment-status-scheduled-dates">
                        Sai em {scheduledDates.map(formatDate).join(" e ")}
                      </p>
                    )}
                  </div>
                )}
                <div className="rounded-md border border-white/10 bg-black/20 p-3" data-testid="payment-status-custody-blocked">
                  <p className="text-[11px] uppercase text-muted-foreground mb-1">Custódia</p>
                  <p className="text-sm font-semibold text-amber-300">{blockedCustody} bloqueada(s)</p>
                </div>
                <div className="rounded-md border border-white/10 bg-black/20 p-3" data-testid="payment-status-transfer-blocked">
                  <p className="text-[11px] uppercase text-muted-foreground mb-1">Pendências</p>
                  <p className="text-sm font-semibold text-red-300">{transferBlocked} item(ns)</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2 min-w-0">
                {isPaid ? (
                  <p className="text-sm font-semibold text-blue-400 flex items-center gap-2" data-testid="payment-pix-title">
                    <CheckCircle2 className="w-4 h-4" /> PIX pago — pagamento processado
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-emerald-400 flex items-center gap-2" data-testid="payment-pix-title"><QrCode className="w-4 h-4" /> PIX/Boleto gerado</p>
                )}
                {!isPaid && data.pix_emv && <p className="text-xs font-mono text-muted-foreground break-all" data-testid="payment-pix-code">{data.pix_emv}</p>}
                {!isPaid && data.boleto_digitable && <p className="text-xs font-mono text-muted-foreground break-all" data-testid="payment-boleto-digitable">{data.boleto_digitable}</p>}
                {data.boleto_value && (
                  paymentResults?.gross_up?.total_fees > 0 ? (
                    <div className="text-xs space-y-0.5" data-testid="payment-pix-breakdown">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>Folha a pagar (após desc/acrésc):</span>
                        <span className="font-mono text-foreground">{formatCurrency(paymentResults.gross_up.payroll_value)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>Taxa Kredz ({paymentResults.gross_up.num_transactions}× {formatCurrency(paymentResults.gross_up.fee_per_transaction)}):</span>
                        <span className="font-mono text-cyan-300">+ {formatCurrency(paymentResults.gross_up.total_fees)}</span>
                      </div>
                      <div className="flex items-center gap-2 pt-0.5 border-t border-white/5 mt-1">
                        <span className="text-foreground font-semibold">Total PIX:</span>
                        <span className="font-mono text-cyan-300 font-bold" data-testid="payment-pix-value">{formatCurrency(data.boleto_value)}</span>
                      </div>
                      {paymentResults.gross_up.num_credor_transfers_absorbed > 0 && (
                        <p className="text-[10px] text-muted-foreground/70 italic pt-1" data-testid="credor-absorbed-note">
                          + {paymentResults.gross_up.num_credor_transfers_absorbed} transferência(s) p/ credor: taxa absorvida pela Kredz
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="payment-pix-value">Valor: {formatCurrency(data.boleto_value)}</p>
                  )
                )}
                {data.payment_sent_at && <p className="text-xs text-muted-foreground">Enviado em: {formatDateTime(data.payment_sent_at)}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!isPaid && (data.pix_url || data.pix_emv) && (
                  <Button size="sm" variant="outline" onClick={() => setShowQrDialog(true)}
                    data-testid="open-qr-dialog-btn" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                    <QrCode className="w-4 h-4 mr-1" /> Ver QR
                  </Button>
                )}
                {!isPaid && data.pix_emv && (
                  <Button size="sm" variant="outline" onClick={copyPixCode}
                    data-testid="copy-pix-code-btn" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                    <Copy className="w-4 h-4 mr-1" /> Copiar PIX
                  </Button>
                )}
                {!isPaid && data.boleto_pdf && !data.pix_url && (
                  <Button size="sm" variant="outline" onClick={() => window.open(data.boleto_pdf, "_blank")}
                    data-testid="view-boleto-btn" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                    <FileText className="w-4 h-4 mr-1" /> Ver Boleto
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => navigate("/company/payments")}
                  data-testid="goto-payments-tab-btn" className="border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5">
                  <Wallet className="w-4 h-4 mr-1" /> Aba Pagamentos
                </Button>
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Month Selector */}
      <Card className="border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month-btn"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="text-lg font-semibold">Competência: {MONTHS[month - 1]} / {year}</span>
                {isCurrent && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/15 text-yellow-400"><AlertCircle className="w-3 h-3" /> Em andamento</span>}
                {data?.is_closed && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/15 text-emerald-400"><Lock className="w-3 h-3" /> Fechado</span>}
              </div>
              <span className="text-xs text-muted-foreground">
                Liquidação: {MONTHS[liquidationMonth - 1]} / {liquidationYear}
              </span>
            </div>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month-btn"><ArrowRight className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {data?.totals && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Folha Total</p>
              <p className="text-xl font-bold">{formatCurrency(t.folha_total)}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.employee_count} colaborador{t.employee_count !== 1 ? "es" : ""}</p>
            </CardContent>
          </Card>
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Descontos</p>
              <p className="text-xl font-bold text-red-400">{formatCurrency(t.total_discounts)}</p>
            </CardContent>
          </Card>
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Acréscimos</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(t.total_acrescimos || 0)}</p>
            </CardContent>
          </Card>
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Antecipações</p>
              <p className="text-xl font-bold text-blue-400">{formatCurrency(t.total_anticipations)}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.anticipation_count} antecipação{t.anticipation_count !== 1 ? "ões" : ""}</p>
            </CardContent>
          </Card>
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Valor a Pagar</p>
              <p className="text-xl font-bold text-orange-400" data-testid="valor-a-pagar">{formatCurrency(t.total_net + t.total_anticipations)}</p>
              <p className="text-xs text-muted-foreground mt-1">Folha após desc. e acrésc.</p>
            </CardContent>
          </Card>
          <Card className="border-white/5 card-interactive">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Desp. Acessórias</p>
              <p className="text-xl font-bold text-purple-400">{formatCurrency(t.total_despesas_acessorias || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Não afeta pagamento</p>
            </CardContent>
          </Card>
          {data?.gross_up_preview ? (
            <Card className="border-cyan-500/30 bg-gradient-to-br from-cyan-500/8 to-blue-500/5 card-interactive lg:col-span-2"
                  data-testid="gross-up-preview-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-cyan-300">Total a Pagar (com Taxa Kredz)</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertCircle className="w-3.5 h-3.5 text-cyan-300/70 shrink-0 cursor-help" data-testid="gross-up-tooltip-trigger" />
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs text-xs">
                        A taxa Kredz é somada ao valor do PIX para que o saldo da
                        empresa não sofra desconto separado. <br /><br />
                        Fórmula: <span className="font-mono">taxa × (1 cash-in + N cashouts ativos)</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-2xl font-extrabold text-cyan-300" data-testid="gross-up-total">
                  {formatCurrency(data.gross_up_preview.total_with_fees_estimate)}
                </p>
                <div className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Folha a pagar (após desc/acrésc)</span>
                    <span className="font-mono text-foreground" data-testid="gross-up-payroll">{formatCurrency(data.gross_up_preview.payroll_value)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      Taxa Kredz · {data.gross_up_preview.num_transactions_estimate}× {formatCurrency(data.gross_up_preview.fee_per_transaction)}
                    </span>
                    <span className="font-mono text-cyan-300" data-testid="gross-up-fee-total">+ {formatCurrency(data.gross_up_preview.gross_up_total_estimate)}</span>
                  </div>
                  <div className="pt-1 text-[10px] text-muted-foreground/70 italic">
                    Estimativa: valor exato calculado na geração do PIX.
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-white/5 card-interactive">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Custo Total</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(t.folha_total + (t.total_despesas_acessorias || 0))}</p>
                <p className="text-xs text-muted-foreground mt-1">Folha + Desp. Acessórias</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Table */}
      <Card className="border-white/5">
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-400" /> Detalhamento por Colaborador
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-white/5">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchFilter} onChange={(e) => { setSearchFilter(e.target.value); setFechPage(1); }}
                placeholder="Buscar colaborador..." className="pl-9 h-8 bg-black/20 border-white/10 text-xs" />
            </div>
            <Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setFechPage(1); }}>
              <SelectTrigger className="w-[150px] h-8 bg-black/20 border-white/10 text-xs"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os departamentos</SelectItem>
                {[...new Set((data?.items || []).map(i => i.department || "Sem departamento"))].sort().map(d =>
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[100px] h-8 bg-black/20 border-white/10 text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">PF e PJ</SelectItem>
                <SelectItem value="PF">PF</SelectItem>
                <SelectItem value="PJ">PJ</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
          <Table className="min-w-[600px] [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {[
                  { key: "employee_name", label: "Colaborador", align: "" },
                  { key: "gross", label: "Salário", align: "text-right" },
                  { key: "discounts_total", label: "Descontos", align: "text-right" },
                  { key: "acrescimos_total", label: "Acréscimos", align: "text-right" },
                  { key: "anticipations_total", label: "Antecipações", align: "text-right" },
                  { key: "despesas_acessorias_total", label: "Desp. Acessórias", align: "text-right" },
                  { key: "net_value", label: "Valor Líquido", align: "text-right" },
                ].map(col => (
                  <TableHead key={col.key}
                    className={`text-xs uppercase tracking-wider text-muted-foreground ${col.align} cursor-pointer hover:text-foreground transition-colors select-none`}
                    onClick={() => {
                      if (sortField === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc");
                      else { setSortField(col.key); setSortDir(col.key === "employee_name" ? "asc" : "desc"); }
                      setFechPage(1);
                    }}>
                    {col.label} {sortField === col.key && (sortDir === "asc" ? "↑" : "↓")}
                  </TableHead>
                ))}
                {canEdit && data?.is_editable && <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const items = data?.items || [];
                const filteredCount = items.length;
                return !filteredCount ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    {data?.items?.length ? "Nenhum resultado para os filtros aplicados" : "Nenhum colaborador neste período"}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {items.map((item) => (
                    <TableRow key={item.employee_id} data-testid={`fechamento-row-${item.employee_id}`}
                      className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium">{item.employee_name}</p>
                            {custodyItems.find(c => c.employee_id === item.employee_id && c.blocked) && (
                              <TooltipProvider><Tooltip><TooltipTrigger>
                                <Lock className="w-3 h-3 text-purple-400" />
                              </TooltipTrigger><TooltipContent><p className="text-xs">Valor em custódia</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                          </div>
                          {item.discounts_items?.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {item.discounts_items.map((d) => (
                                <div key={d.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className={d.entry_type === "despesa_acessoria" ? "text-purple-400" : d.entry_type === "acrescimo" ? "text-emerald-400" : "text-red-400"}>
                                    {d.entry_type === "despesa_acessoria" ? "" : d.entry_type === "acrescimo" ? "+" : "-"}{formatCurrency(d.amount)}
                                  </span>
                                  <span className="flex-1">{d.description}</span>
                                  {d.recurrence && d.recurrence !== "unico" && <span className="text-[10px] text-blue-400/50">recorrente</span>}
                                  {canEdit && data?.is_editable && (
                                    <button data-testid={`edit-discount-${d.id}`}
                                      onClick={() => setEditDiscountDialog({ ...d, month: monthStr })}
                                      className="text-muted-foreground/50 hover:text-foreground"><Edit className="w-3 h-3" /></button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(item.gross)}</TableCell>
                      <TableCell className="text-right text-red-400">{item.discounts_total > 0 ? `-${formatCurrency(item.discounts_total)}` : "-"}</TableCell>
                      <TableCell className="text-right text-emerald-400">{(item.acrescimos_total || 0) > 0 ? `+${formatCurrency(item.acrescimos_total)}` : "-"}</TableCell>
                      <TableCell className="text-right text-blue-400">{item.anticipations_total > 0 ? `-${formatCurrency(item.anticipations_total)}` : "-"}</TableCell>
                      <TableCell className="text-right text-purple-400">{(item.despesas_acessorias_total || 0) > 0 ? formatCurrency(item.despesas_acessorias_total) : "-"}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-400">{formatCurrency(item.net_value)}</TableCell>
                      {canEdit && data?.is_editable && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" data-testid={`add-discount-btn-${item.employee_id}`}
                            onClick={() => { setDiscountDialog(item.employee_id); setDiscountForm({ description: "", amount: "", entry_type: "desconto", value_type: "fixed", recurrence: "unico", recurrence_months: "" }); }}
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
                            <Plus className="w-3 h-3 mr-1" /> Lançamento
                          </Button>
                        </TableCell>
                      )}
                      {canEdit && data?.sent_for_payment && custodyItems.find(c => c.employee_id === item.employee_id && c.blocked) && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" data-testid={`release-custody-${item.employee_id}`}
                            onClick={() => handleReleaseCustodyEmployee(item.employee_id)}
                            className="h-7 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10">
                            <Unlock className="w-3 h-3 mr-1" /> Liberar
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="border-white/5 bg-white/[0.03] font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{formatCurrency(t.folha_total)}</TableCell>
                    <TableCell className="text-right text-red-400">{t.total_discounts > 0 ? `-${formatCurrency(t.total_discounts)}` : "-"}</TableCell>
                    <TableCell className="text-right text-emerald-400">{(t.total_acrescimos || 0) > 0 ? `+${formatCurrency(t.total_acrescimos)}` : "-"}</TableCell>
                    <TableCell className="text-right text-blue-400">{t.total_anticipations > 0 ? `-${formatCurrency(t.total_anticipations)}` : "-"}</TableCell>
                    <TableCell className="text-right text-purple-400">{(t.total_despesas_acessorias || 0) > 0 ? formatCurrency(t.total_despesas_acessorias) : "-"}</TableCell>
                    <TableCell className="text-right text-emerald-400">{formatCurrency(t.total_net)}</TableCell>
                    {canEdit && !data?.is_closed && <TableCell />}
                  </TableRow>
                </>
              );
              })()}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {data?.pagination && <Pagination page={data.pagination.page} totalPages={data.pagination.total_pages}
        total={data.pagination.total} pageSize={data.pagination.page_size} onPageChange={setFechPage} />}

      {/* Add Discount/Acréscimo Dialog */}
      <Dialog open={!!discountDialog} onOpenChange={() => setDiscountDialog(null)}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="add-discount-dialog">
          <DialogHeader>
            <DialogTitle>Novo Lançamento</DialogTitle>
            <DialogDescription>Competência: {MONTHS[month - 1]} {year}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={discountForm.entry_type} onValueChange={(v) => setDiscountForm({ ...discountForm, entry_type: v })}>
                <SelectTrigger data-testid="discount-type-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="desconto">Desconto (será abatido do salário)</SelectItem>
                  <SelectItem value="acrescimo">Acréscimo (será somado ao salário)</SelectItem>
                  <SelectItem value="despesa_acessoria">Despesa Acessória (apenas visão dashboard)</SelectItem>
                </SelectContent>
              </Select>
              {discountForm.entry_type === "desconto" && (
                <p className="text-xs text-yellow-400 mt-2" data-testid="discount-type-alert">
                  Atenção! Isso vai ser descontado do valor do salário base do colaborador a ser pago aqui pela Kredz. Se essa despesa for como um VR que você paga a outra empresa, e NÃO desconta isso do salário base do colaborador, lance a mesma como Despesa Acessória.
                </p>
              )}
              {discountForm.entry_type === "acrescimo" && (
                <p className="text-xs text-yellow-400 mt-2" data-testid="acrescimo-type-alert">
                  Atenção! Esse valor vai ser somado ao salário base do colaborador a ser pago aqui pela Kredz. Se isso for algum benefício que você paga diretamente a algum outro fornecedor como um VR, e NÃO desconta isso do salário base do colaborador, lance a mesma como Despesa Acessória.
                </p>
              )}
              {discountForm.entry_type === "despesa_acessoria" && (
                <p className="text-xs text-yellow-400 mt-2" data-testid="despesa-type-alert">
                  Atenção! Despesas acessórias não afetam o valor líquido que o colaborador vai receber aqui pela Kredz. Elas servem apenas para controle interno de custo e vão afetar sua visão no dashboard. Se você quiser que a mesma seja descontada do salário base do colaborador, lance a mesma como Desconto (será abatido do salário).
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              {discountForm.entry_type === "desconto" ? (
                <DiscountCategorySelect
                  value={discountForm.description}
                  onChange={(v) => setDiscountForm({ ...discountForm, description: v })}
                  options={["IRRF", "INSS", "Consignado Privado", "Plano de Saúde", "Plano Odontológico", "Vale Transporte", "Vale Refeição", "Vale Alimentação", "Estacionamento", "Adiantamento", "Pensão Alimentícia"]}
                  placeholder="Selecione uma categoria..."
                  customPlaceholder="Digite a descrição"
                />
              ) : discountForm.entry_type === "despesa_acessoria" ? (
                <div className="space-y-2">
                  <DiscountCategorySelect
                    value={discountForm.description}
                    onChange={(v) => setDiscountForm({ ...discountForm, description: v })}
                    options={["Estacionamento (sem desconto na folha)", "Plano de Saúde (sem desconto em folha)", "Auxílio Moradia (sem desconto em folha)"]}
                    placeholder="Selecione uma categoria..."
                    customPlaceholder="Ex: FGTS, Seguro de vida"
                  />
                </div>
              ) : (
                <Input data-testid="discount-description-input" value={discountForm.description}
                  onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                  placeholder="Ex: Reembolso, Bônus, Horas extras" className="bg-black/20 border-white/10" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{discountForm.value_type === "percent" ? "Valor (%)" : "Valor (R$)"}</Label>
                {discountForm.value_type === "percent" ? (
                  <div className="relative">
                    <Input data-testid="discount-amount-input" type="number" step="0.01" min="0"
                      value={discountForm.amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
                      placeholder="0,00" className="bg-black/20 border-white/10 pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                ) : (
                  <CurrencyInput data-testid="discount-amount-input"
                    value={discountForm.amount}
                    onChange={(v) => setDiscountForm({ ...discountForm, amount: v })}
                    placeholder="500,00" className="bg-black/20 border-white/10" />
                )}
              </div>
              <div className="space-y-2">
                <Label>Tipo de Valor</Label>
                <Select value={discountForm.value_type} onValueChange={(v) => setDiscountForm({ ...discountForm, value_type: v })}>
                  <SelectTrigger data-testid="discount-value-type-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    <SelectItem value="percent">% do salário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Recorrência</Label>
              <Select value={discountForm.recurrence} onValueChange={(v) => setDiscountForm({ ...discountForm, recurrence: v })}>
                <SelectTrigger data-testid="discount-recurrence-select" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unico">Único (somente este mês)</SelectItem>
                  <SelectItem value="recorrente_prazo">Recorrente com prazo</SelectItem>
                  <SelectItem value="recorrente_indeterminado">Recorrente indeterminado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {discountForm.recurrence === "recorrente_prazo" && (
              <div className="space-y-2">
                <Label>Quantidade de meses</Label>
                <Input data-testid="discount-months-input" type="number" min="1" max="60"
                  value={discountForm.recurrence_months}
                  onChange={(e) => setDiscountForm({ ...discountForm, recurrence_months: e.target.value })}
                  placeholder="Ex: 6" className="bg-black/20 border-white/10" />
              </div>
            )}
            {discountForm.recurrence === "recorrente_indeterminado" && (
              <p className="text-xs text-muted-foreground">Será lançado automaticamente nos próximos 12 meses. Pode ser cancelado a qualquer momento.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountDialog(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="save-discount-btn" onClick={handleAddDiscount} disabled={savingDiscount}
              className={`${discountForm.entry_type === "desconto" ? "bg-red-600 hover:bg-red-700" : discountForm.entry_type === "acrescimo" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-purple-600 hover:bg-purple-700"} text-white font-semibold`}>
              {savingDiscount ? <div className="loading-spinner" /> : `Adicionar ${discountForm.entry_type === "desconto" ? "Desconto" : discountForm.entry_type === "acrescimo" ? "Acréscimo" : "Despesa"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Month Dialog */}
      <Dialog open={closeDialog} onOpenChange={() => setCloseDialog(false)}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="close-month-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" /> Fechar Mês
            </DialogTitle>
            <DialogDescription>
              Competência: {MONTHS[month - 1]} {year} · Liquidação: {MONTHS[liquidationMonth - 1]} {liquidationYear}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-500/5 border border-orange-500/10">
                <Lock className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <p className="text-sm"><strong>Bloqueio de alterações:</strong> Não poderão mais ser lançados descontos ou alterações em valores para este mês.</p>
              </div>
              {isAheadOfExpected && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="close-month-ahead-warning">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-red-400 font-semibold">Atenção: mês adiantado!</p>
                    <p className="text-muted-foreground mt-1">
                      Você está fechando <strong>{MONTHS[month - 1]}/{year}</strong> (liquidação em {MONTHS[liquidationMonth - 1]}/{liquidationYear}),
                      mas o mês esperado para fechar agora seria <strong>{MONTHS[expectedCompetencia - 1]}/{expectedCompetenciaYear}</strong> (liquidação em {MONTHS[currentMonth - 1]}/{currentYear}).
                    </p>
                    <p className="text-muted-foreground mt-1">Confirme que deseja fechar este mês e não o anterior.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold">Confirme as datas de pagamento:</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Pagamento PF</Label>
                  <Input data-testid="close-payment-date-pf" type="date" value={paymentDatePf}
                    onChange={(e) => setPaymentDatePf(e.target.value)}
                    className="bg-black/20 border-white/10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pagamento PJ</Label>
                  <Input data-testid="close-payment-date-pj" type="date" value={paymentDatePj}
                    onChange={(e) => setPaymentDatePj(e.target.value)}
                    className="bg-black/20 border-white/10" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">Datas sugeridas com base no fluxo de pagamento configurado, considerando feriados nacionais.</p>
              {/* C7, carimbo (iii)=C do owner: as DUAS datas passaram a valer de
                  verdade. O aviso existe porque o comportamento MUDOU — antes o
                  mês inteiro era pago na data PF; agora cada tipo sai na sua, e
                  a cobrança vence na mais cedo para o dinheiro estar lá quando a
                  segunda vencer. Sem isto, quem põe PJ para o dia 20 acha que
                  paga o PIX no dia 20. */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/15" data-testid="close-two-dates-notice">
                <CalendarClock className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <p className="text-cyan-300 font-semibold">Cada tipo é pago na sua data</p>
                  <p className="text-muted-foreground mt-1">
                    Colaboradores PF recebem na data PF e PJ na data PJ — uma não substitui a outra.
                    O PIX de cobrança é <strong>um só</strong> e vence na <strong>data mais cedo entre os tipos que
                    existem neste fechamento</strong> — para o dinheiro já estar em conta quando a segunda chegar.
                    Data de um tipo que não tem ninguém no mês é ignorada na escolha.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Se houver colaboradores dos dois tipos, as duas datas são obrigatórias.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-close-month-btn" onClick={handleCloseMonth} disabled={closing}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold">
              {closing ? <div className="loading-spinner" /> : "Confirmar Fechamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Month Dialog */}
      <Dialog open={reopenDialog} onOpenChange={() => setReopenDialog(false)}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="reopen-month-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className={`w-5 h-5 ${data?.sent_for_payment ? "text-red-400" : "text-yellow-400"}`} /> Reabrir Mês
            </DialogTitle>
            <DialogDescription>
              Competência: {MONTHS[month - 1]} {year} · Liquidação: {MONTHS[liquidationMonth - 1]} {liquidationYear}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Ao reabrir o mês:</p>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
              <Unlock className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
              <p className="text-sm"><strong>Desbloqueio de alterações:</strong> Será possível adicionar/remover descontos e fazer ajustes novamente.</p>
            </div>
            {data?.sent_for_payment && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p><strong className="text-red-400">PIX/Boleto será cancelado:</strong> O pagamento gerado anteriormente será invalidado operacionalmente.</p>
                  <p className="text-muted-foreground mt-1">Após fazer as alterações, feche o mês novamente e envie para pagamento para gerar um novo PIX/Boleto.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenDialog(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-reopen-month-btn" onClick={handleReopenMonth} disabled={reopening}
              className={`${data?.sent_for_payment ? "bg-red-600 hover:bg-red-700" : "bg-yellow-600 hover:bg-yellow-700"} text-white font-semibold`}>
              {reopening ? <div className="loading-spinner" /> : data?.sent_for_payment ? "Reabrir e Cancelar PIX/Boleto" : "Confirmar Reabertura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Discount Dialog */}
      <EditDiscountDialog
        discount={editDiscountDialog}
        monthStr={monthStr}
        onClose={() => setEditDiscountDialog(null)}
        onSuccess={() => { setEditDiscountDialog(null); fetchData(); }}
      />

      {/* QR Code PIX Dialog — auto-opens after send-payment, reopenable via "Ver QR" */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="pix-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-400" /> PIX — {MONTHS[month - 1]}/{year}
            </DialogTitle>
            <DialogDescription>Escaneie o QR Code ou use o copia-e-cola para pagar o fechamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-center">
              {data?.pix_url ? (
                <img src={data.pix_url} alt="QR Code PIX" data-testid="pix-qr-image"
                  className="w-56 h-56 rounded-lg border border-white/10 bg-white p-3" />
              ) : (
                <div className="w-56 h-56 rounded-lg border border-white/10 bg-black/20 flex items-center justify-center text-muted-foreground text-xs">
                  QR Code indisponível
                </div>
              )}
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase text-muted-foreground">Valor</span>
                <span className="text-sm font-semibold" data-testid="pix-dialog-amount">{formatCurrency(data?.boleto_value || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase text-muted-foreground">Status</span>
                <span className="text-xs" data-testid="pix-dialog-status">{paymentStatus.label}</span>
              </div>
              {data?.pix_emv && (
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground mb-1">Copia e cola</p>
                  <p className="text-[10px] font-mono break-all text-muted-foreground" data-testid="pix-dialog-emv">{data.pix_emv}</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {data?.pix_emv && (
              <Button onClick={copyPixCode} data-testid="pix-dialog-copy-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Copy className="w-4 h-4 mr-2" /> Copiar PIX
              </Button>
            )}
            {data?.pix_url && (
              <Button variant="outline" onClick={() => window.open(data.pix_url, "_blank")}
                data-testid="pix-dialog-open-btn" className="border-white/10">
                <ExternalLink className="w-4 h-4 mr-2" /> Abrir em nova aba
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditDiscountDialog({ discount, monthStr, onClose, onSuccess }) {
  const [action, setAction] = useState(null);
  const [newAmount, setNewAmount] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (discount) {
      setAction(null);
      setNewAmount(String(discount.amount || ""));
      setEndMonth("");
    }
  }, [discount]);

  if (!discount) return null;

  const isRecurrent = discount.recurrence && discount.recurrence !== "unico";
  const ruleId = discount.from_rule_id || (discount.from_rule ? discount.id : null);
  const targetId = ruleId || discount.id;

  const handleAction = async (act, extra = {}) => {
    setSaving(true);
    try {
      const res = await api.put(`/fechamento/discounts/${targetId}`, { action: act, month: monthStr, ...extra });
      toast.success(res.data.message);
      onSuccess();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao processar");
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!discount} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md border-white/10" data-testid="edit-discount-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Editar Lançamento</DialogTitle>
          <DialogDescription>
            {discount.description} — {formatCurrency(discount.amount)}
            {isRecurrent && <span className="ml-2 text-blue-400">(recorrente)</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-3">
          {/* Edit Value */}
          <button onClick={() => setAction(action === "edit" ? null : "edit")}
            className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${action === "edit" ? "border-blue-500/40 bg-blue-500/5" : "border-white/5 hover:bg-white/[0.02]"}`}>
            <p className="font-medium">Editar valor</p>
            <p className="text-[10px] text-muted-foreground">{isRecurrent ? "Altera o valor da regra (afeta este mês + futuros)" : "Altera o valor deste lançamento"}</p>
          </button>
          {action === "edit" && (
            <div className="flex gap-2 px-3">
              <CurrencyInput value={newAmount} onChange={setNewAmount} placeholder="Novo valor" className="bg-black/20 border-white/10 flex-1" />
              <Button size="sm" disabled={saving || !newAmount} onClick={() => handleAction("edit_value", { amount: parseFloat(newAmount) })}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {saving ? <div className="loading-spinner" /> : "Salvar"}
              </Button>
            </div>
          )}

          {/* Set End Month (only recurrent) */}
          {isRecurrent && (
            <>
              <button onClick={() => setAction(action === "end" ? null : "end")}
                className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${action === "end" ? "border-yellow-500/40 bg-yellow-500/5" : "border-white/5 hover:bg-white/[0.02]"}`}>
                <p className="font-medium">Definir data fim</p>
                <p className="text-[10px] text-muted-foreground">Define até quando a recorrência vale</p>
              </button>
              {action === "end" && (
                <div className="flex gap-2 px-3">
                  <Input type="month" value={endMonth} onChange={(e) => setEndMonth(e.target.value)}
                    className="bg-black/20 border-white/10 flex-1 text-xs" />
                  <Button size="sm" disabled={saving || !endMonth} onClick={() => handleAction("set_end_month", { end_month: endMonth })}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs">
                    {saving ? <div className="loading-spinner" /> : "Definir"}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Remove Options */}
          <div className="pt-1 border-t border-white/5">
            <p className="text-xs text-muted-foreground mb-2 px-1">Remover</p>
            {isRecurrent ? (
              <div className="space-y-1.5">
                <button onClick={() => handleAction("remove_this_month")} disabled={saving}
                  className="w-full text-left p-2.5 rounded-lg border border-white/5 hover:bg-red-500/5 text-xs transition-colors">
                  <span className="font-medium text-red-400">Só este mês</span>
                  <span className="text-muted-foreground ml-2">A regra continua nos próximos</span>
                </button>
                <button onClick={() => handleAction("remove_forward")} disabled={saving}
                  className="w-full text-left p-2.5 rounded-lg border border-white/5 hover:bg-red-500/5 text-xs transition-colors">
                  <span className="font-medium text-red-400">Deste mês em diante</span>
                  <span className="text-muted-foreground ml-2">Encerra a recorrência</span>
                </button>
                <button onClick={() => { if (window.confirm("Tem certeza? Lançamentos em meses fechados serão preservados.")) handleAction("remove_all"); }} disabled={saving}
                  className="w-full text-left p-2.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 text-xs transition-colors">
                  <span className="font-medium text-red-400">Todos (cancelar regra)</span>
                  <span className="text-muted-foreground ml-2">Meses fechados são preservados</span>
                </button>
              </div>
            ) : (
              <button onClick={() => handleAction("remove_all")} disabled={saving}
                className="w-full text-left p-2.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 text-xs transition-colors">
                <span className="font-medium text-red-400">Remover lançamento</span>
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
